import React, { useRef, useEffect } from "react";
import Navbar from "../Navbar";
import { SvgElement, icontypesEnum } from "../assets/svgElement";
import Footer from "../Footer";
import { SEO } from "../SEO";

function ContactUs() {
  // Animation refs
  const heroRef = useRef(null);
  const contactRef = useRef(null);

  // Animation observer effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          // Hero animation
          if (entry.target === heroRef.current) {
            const title = entry.target.querySelector(".hero-title");
            const subtitle = entry.target.querySelector(".hero-subtitle");

            if (title) {
              setTimeout(() => {
                title.classList.add("animate-fade-in-up");
              }, 100);
            }

            if (subtitle) {
              setTimeout(() => {
                subtitle.classList.add("animate-fade-in-up");
              }, 250);
            }
          }

          // Contact section animation
          if (entry.target === contactRef.current) {
            const contactCard = entry.target.querySelector(".contact-card");
            const formCard = entry.target.querySelector(".form-card");

            if (contactCard) {
              setTimeout(() => {
                contactCard.classList.add("animate-slide-in-left");
              }, 100);
            }

            if (formCard) {
              setTimeout(() => {
                formCard.classList.add("animate-slide-in-right");
              }, 200);
            }

            // Contact info items
            const contactItems = entry.target.querySelectorAll(".contact-item");
            contactItems.forEach((item: any, index: any) => {
              setTimeout(
                () => {
                  item.classList.add("animate-fade-in-up");
                },
                400 + index * 100,
              );
            });

            // Social icons
            const socialIcons = entry.target.querySelectorAll(".social-icon");
            socialIcons.forEach((icon: any, index: any) => {
              setTimeout(
                () => {
                  icon.classList.add("animate-scale-in");
                },
                700 + index * 100,
              );
            });

            // Form fields
            const formFields = entry.target.querySelectorAll(".form-field");
            formFields.forEach((field: any, index: any) => {
              setTimeout(
                () => {
                  field.classList.add("animate-fade-in-up");
                },
                400 + index * 80,
              );
            });

            const submitBtn = entry.target.querySelector(".submit-btn");
            if (submitBtn) {
              setTimeout(() => {
                submitBtn.classList.add("animate-scale-in");
              }, 1000);
            }
          }

          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (heroRef.current) observer.observe(heroRef.current);
    if (contactRef.current) observer.observe(contactRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .initial-hidden {
          opacity: 0;
          transform: translateY(20px);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-scale-in {
          animation: scaleIn 0.5s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.7s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.7s ease-out forwards;
        }
      `}</style>

      <SEO
        title="Contact Us | Afripay"
        description="Contact us with any enquiries or issues on Afripay."
        canonical="https://goafripay.com/contact-us"
      />

      <section className="bg-gradient-to-b from-[#FAFBFC] to-[#F8FFFE]">
        <Navbar />

        {/* Hero Section */}
        <div ref={heroRef} className="pt-[120px] md:pt-[140px] pb-20">
          <div className="flex flex-col justify-center items-center px-8">
            <h1 className="hero-title initial-hidden text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-[#0a0a0a] leading-tight tracking-[-0.02em]">
              Contact Us
            </h1>
            <p className="hero-subtitle initial-hidden text-base sm:text-lg text-[#525252] text-center font-body font-medium leading-relaxed max-w-2xl mt-4">
              Any questions or remarks? Just write us a message!
            </p>
          </div>

          {/* Contact Section */}
          <div ref={contactRef} className="mt-14 px-6">
            <div className="rounded-[20px] bg-transparent p-2 md:mx-[95px] shadow-lg">
              <div className="flex lg:flex-row flex-col lg:justify-between gap-10">
                {/* Contact Info Card */}
                {/*  */}
                <div className="contact-card initial-hidden bg-[#0B7F47] hover:bg-[#0a3d26]  text-white rounded-2xl md:w-[500px] lg:col-span-5 lg:px-8 px-6 py-8 shadow-xl">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-display font-bold mb-2">
                    Send Message
                  </h3>
                  <p className="text-sm sm:text-base font-body font-medium opacity-90">
                    Fill up the form and our Team will get back to you within 24
                    Hours
                  </p>

                  <div className="mt-10">
                    {/* Phone */}
                    <div className="contact-item initial-hidden flex space-x-4 mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.62 10.79C8.06 13.62 10.38 15.93 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z"
                          fill="white"
                        />
                      </svg>
                      <div>
                        <p className="text-[12px] md:text-[14px] font-normal">
                          +234 906 178 1999
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="contact-item initial-hidden flex space-x-4 mb-4">
                      <svg
                        width="21"
                        height="21"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18.5769 3.23077H2.42308C1.05 3.23077 0 4.28077 0 5.65385V15.3462C0 16.7192 1.05 17.7692 2.42308 17.7692H18.5769C19.95 17.7692 21 16.7192 21 15.3462V5.65385C21 4.28077 19.95 3.23077 18.5769 3.23077ZM19.2231 15.6692L12.9231 11.1462L10.5 12.7615L7.99615 11.1462L1.77692 15.6692L6.86538 10.4192L0.646154 5.57308L10.5 10.9039L20.2731 5.65385L14.1346 10.5L19.2231 15.6692Z"
                          fill="white"
                        />
                      </svg>
                      <p className="text-[12px] md:text-[14px] font-normal">
                        hello@goafripay.com
                      </p>
                    </div>

                    {/* Address */}
                    <div className="contact-item initial-hidden flex space-x-4 mb-4 items-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_512_5455)">
                          <path
                            d="M12 2C14.3869 2 16.6761 2.94821 18.364 4.63604C20.0518 6.32387 21 8.61305 21 11C21 14.074 19.324 16.59 17.558 18.395C16.6755 19.2869 15.7128 20.0956 14.682 20.811L14.256 21.101L14.056 21.234L13.679 21.474L13.343 21.679L12.927 21.921C12.6445 22.0818 12.325 22.1663 12 22.1663C11.675 22.1663 11.3555 22.0818 11.073 21.921L10.657 21.679L10.137 21.359L9.945 21.234L9.535 20.961C8.42298 20.2083 7.38707 19.3489 6.442 18.395C4.676 16.588 3 14.074 3 11C3 8.61305 3.94821 6.32387 5.63604 4.63604C7.32387 2.94821 9.61305 2 12 2ZM12 8C11.606 8 11.2159 8.0776 10.8519 8.22836C10.488 8.37913 10.1573 8.6001 9.87868 8.87868C9.6001 9.15726 9.37913 9.48797 9.22836 9.85195C9.0776 10.2159 9 10.606 9 11C9 11.394 9.0776 11.7841 9.22836 12.1481C9.37913 12.512 9.6001 12.8427 9.87868 13.1213C10.1573 13.3999 10.488 13.6209 10.8519 13.7716C11.2159 13.9224 11.606 14 12 14C12.7956 14 13.5587 13.6839 14.1213 13.1213C14.6839 12.5587 15 11.7956 15 11C15 10.2044 14.6839 9.44129 14.1213 8.87868C13.5587 8.31607 12.7956 8 12 8Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_512_5455">
                            <rect width="24" height="24" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <div>
                        <p className="text-[12px] md:text-[14px] font-normal">
                          Lagos Office | 3, Samuel Ajakaiye street, Lagos
                          Nigeria
                        </p>
                        <p className="text-[12px] md:text-[14px] font-normal mt-1">
                          London Office | 124-128 City Road, London, EC1V 2NX
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Social Media */}
                  <div className="mt-10">
                    <div className="flex space-x-5">
                      <a
                        href="https://www.instagram.com/afripaygo?igsh=amZmNG80djRlNDh6"
                        target="_blank"
                        className="social-icon initial-hidden"
                      >
                        <SvgElement type={icontypesEnum.INSTAGRAM} />
                      </a>
                      <a
                        href="https://www.facebook.com/share/1Ai9hjBSBJ/?mibextid=wwXIfr"
                        target="_blank"
                        className="social-icon initial-hidden"
                      >
                        <SvgElement type={icontypesEnum.FACEBOOK} />
                      </a>
                      <a
                        href="https://x.com/goafripay?s=11&t=8Y0Pr_VTIrnse_LLkDOq9w"
                        target="_blank"
                        className="social-icon initial-hidden"
                      >
                        <SvgElement type={icontypesEnum.TWITTER} />
                      </a>
                      <a
                        href="https://www.linkedin.com/company/goafripay/"
                        target="_blank"
                        className="social-icon initial-hidden"
                      >
                        <SvgElement type={icontypesEnum.LINKEDIN} />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Form Card */}
                <div className="form-card initial-hidden lg:col-span-7">
                  <div className="">
                    <form>
                      <div className="flex flex-col lg:flex-row lg:justify-between max-w-screen-xl mx-auto mt-8">
                        <div className="relative flex flex-col min-w-0 break-words w-full">
                          <div className="flex-auto px-5 py-10 pt-0">
                            <div className="flex flex-wrap">
                              {/* First Name */}
                              <div className="form-field initial-hidden w-full lg:w-6/12 lg:pr-4">
                                <div className="relative z-0 w-full mb-10 group">
                                  <input
                                    type="text"
                                    name="first_name"
                                    id="first_name"
                                    className="block py-2.5 px-0 w-full text-sm text-[#209D5A] bg-transparent border-0 border-b-2 border-[#D9D9D9] appearance-none dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-[#777777] peer"
                                    placeholder=" "
                                    required
                                  />
                                  <label
                                    htmlFor="first_name"
                                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-[#777777] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                  >
                                    First Name
                                  </label>
                                </div>
                              </div>

                              {/* Last Name */}
                              <div className="form-field  w-full lg:w-6/12">
                                <div className="relative z-0 w-full mb-10 group">
                                  <input
                                    type="text"
                                    name="last_name"
                                    id="last_name"
                                    className="block py-2.5 px-0 w-full text-sm text-[#209D5A] bg-transparent border-0 border-b-2 border-[#D9D9D9] appearance-none dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-[#777777] peer"
                                    placeholder=" "
                                    required
                                  />
                                  <label
                                    htmlFor="last_name"
                                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-[#777777] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                  >
                                    Last Name
                                  </label>
                                </div>
                              </div>

                              {/* Email */}
                              <div className="form-field initial-hidden w-full lg:w-6/12 lg:pr-4">
                                <div className="relative z-0 w-full mb-10 group">
                                  <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    className="block py-2.5 px-0 w-full text-sm text-[#209D5A] bg-transparent border-0 border-b-2 border-[#D9D9D9] appearance-none dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-[#777777] peer"
                                    placeholder=" "
                                    required
                                  />
                                  <label
                                    htmlFor="email"
                                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-[#777777] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                  >
                                    Email Address
                                  </label>
                                </div>
                              </div>

                              {/* Phone Number */}
                              <div className="form-field initial-hidden w-full lg:w-6/12">
                                <div className="relative z-0 w-full mb-10 group">
                                  <input
                                    type="number"
                                    name="phone_number"
                                    id="phone_number"
                                    className="block py-2.5 px-0 w-full text-sm text-[#209D5A] bg-transparent border-0 border-b-2 border-[#D9D9D9] appearance-none dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-[#777777] peer"
                                    placeholder=" "
                                    required
                                  />
                                  <label
                                    htmlFor="phone_number"
                                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-[#777777] peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                  >
                                    Phone Number
                                  </label>
                                </div>
                              </div>

                              {/* Message */}
                              <div className="form-field initial-hidden w-full lg:w-12/12">
                                <div className="relative z-0 w-full mb-10 group">
                                  <input
                                    type="text"
                                    name="message"
                                    id="message"
                                    className="block py-2.5 px-0 w-full text-sm text-[#209D5A] bg-transparent border-0 border-b-2 border-[#D9D9D9] appearance-none dark:text-white dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-[#777777] peer"
                                    placeholder=" "
                                    required
                                  />
                                  <label
                                    htmlFor="message"
                                    className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-focus:text-[#777777] peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                                  >
                                    Message
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="px-5">
                        <button
                          type="submit"
                          className="submit-btn initial-hidden w-full text-white bg-[#0B7F47] hover:bg-[#0a3d26] border-2 border-[#7FBC51] font-medium rounded-[50px] text-sm px-5 py-3 transition-all duration-300 hover:shadow-lg"
                        >
                          Send Message
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default ContactUs;
